import React, { useState, useEffect, useMemo, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { FaUserPlus } from 'react-icons/fa';
import { BsShieldCheck, BsGift } from 'react-icons/bs';
import { MdOutlineNotificationsActive } from 'react-icons/md';
import { showError, showSuccess } from "../utils/toast"; // adjust path
import { useNavigate } from 'react-router-dom';
import dayjs from "dayjs";

const Dashboard = () => {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  const [policies, setPolicies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Add this helper function at the component level (before the return statement)
const truncateTitle = (title, wordLimit = 4) => {
  if (!title) return '';
  const words = title.split(' ');
  if (words.length <= wordLimit) return title;
  return words.slice(0, wordLimit).join(' ') + '...';
};

// Add navigate hook in your component
const navigate = useNavigate();

// Add click handler function
const handleCardClick = (item) => {
  const customerId = user?.id || item.customerId;
  const policyId = item._id;
  const policyNumber = item.policyNumber;
  
  navigate(`/customer/policy-view/${customerId}/${policyId}/${policyNumber}`);
};

const getTimeEmoji = () => {
  const currentHour = new Date().getHours();
  
  if (currentHour >= 5 && currentHour < 12) {
    return "🌤️"; // Sun with clouds for morning
  } else if (currentHour >= 12 && currentHour < 17) {
    return "☀️"; // Complete sun for afternoon
  } else {
    return "🌙✨"; // Moon with stars for evening
  }
};

const today  = dayjs().startOf("day");
const cutoff = today.subtract(10, "day"); // >10 days past = expired

let customersCount   = policies.length;
let dueCount         = 0;  // endDate after today
let overdueCount     = 0;  // endDate on/before today but within last 10 days
let expiredCount     = 0;  // endDate more than 10 days before today
let upcoming48hCount = 0; 
let renewalsData = []; // NEW: endDate is exactly +1 or +2 days from today

policies.forEach((policy) => {
  const rawEnd =
    policy.endDate ||
    policy.policyDetails?.endDate ||
    policy.policyDetails?.end_date;

  const endDate = dayjs(rawEnd).startOf("day");
  if (!endDate.isValid()) return;

  if (endDate.isAfter(today)) {
    dueCount++;

    const diffDays = endDate.diff(today, "day");
    if (diffDays === 1 || diffDays === 2) {
      renewalsData.push(policy)
      upcoming48hCount++;
    }
  } else if (endDate.isSame(today) || endDate.isAfter(cutoff)) {
    overdueCount++;
  } else {
    expiredCount++;
  }
});

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

    const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user"); // if you save user info
    showSuccess("Logged Out successfully")
    window.location.href = "/login";
  };

  const handleMenuClick = (action) => {
    setIsDropdownOpen(false);
    
    // Handle different menu actions
    switch(action) {
      case 'viewProfile':
        // Add your view profile logic here
        console.log('View Profile clicked');
        break;
      case 'updateProfile':
        // Add your update profile logic here
        console.log('Update Profile clicked');
        break;
      case 'updatePassword':
        // Add your update password logic here
        console.log('Update Password clicked');
        break;
      case 'settings':
        // Add your settings logic here
        console.log('Settings clicked');
        break;
      case 'logout':
        // Add your logout logic here
        handleLogout()
        break;
      default:
        break;
    }
  };

    const getGreeting = () => {
    const currentHour = new Date().getHours();
    
    if (currentHour >= 5 && currentHour < 12) {
      return "Good Morning";
    } else if (currentHour >= 12 && currentHour < 17) {
      return "Good Afternoon";
    } else {
      return "Good Evening"; // Changed from "Good Night" to "Good Evening"
    }
  };

    const formatName = (name) => {
    if (!name) return "User";
    
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  
  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.id) {
          console.warn("❌ User ID not found.");
          return;
        }
        console.log("📥 Fetching policies for user ID:", user.id);
        const res = await fetch(`${import.meta.env.VITE_API_URL}/v1/customer/customer-fetch-policy/${user.id}`);
        console.log(res);
        const data = await res.json();
        const next = Array.isArray(data.policyData) ? data.policyData : [];
        setPolicies(next);
        if (res.ok) {
           console.log("✅ Filtered Policies:", data);          
            const companyList = ["All", ...new Set(data.policyData.map(p => p.policyType).filter(policyType => policyType && policyType.trim() !== ""))];
            setCategories(companyList);
        } else {
          console.warn("⚠️ Unexpected policy data structure:", data);
        }
      } catch (err) {
        console.error("🚫 Failed to fetch policies:", err);
      }
    };

    fetchPolicies();
  }, []);

  const FilteredPolicies = useMemo(() => {
    return activeTab === "All"
      ? policies
      : policies.filter(p => p.policyType === activeTab);
  }, [activeTab, policies]);

  // Helper functions
  const getCardColorClass = (policyType, index) => {
    const colorClasses = [
      'card-blue-gradient',
      'card-cyan-gradient', 
      'card-teal-gradient',
      'card-purple-gradient',
      'card-orange-gradient'
    ];
    
    // Assign colors based on policy type or use index for variety
    if (policyType?.toLowerCase().includes('motor')) return 'card-blue-gradient';
    if (policyType?.toLowerCase().includes('health')) return 'card-teal-gradient';
    if (policyType?.toLowerCase().includes('life')) return 'card-purple-gradient';
    if (policyType?.toLowerCase().includes('home')) return 'card-orange-gradient';
    
    // Fallback to cycling through colors
    return colorClasses[index % colorClasses.length];
  };
  
  const getCategoryLabel = (policyType) => {
    if (policyType?.toLowerCase().includes('motor')) return 'Motor Insurance';
    if (policyType?.toLowerCase().includes('health')) return 'Health Insurance';
    if (policyType?.toLowerCase().includes('life')) return 'Life Insurance';
    if (policyType?.toLowerCase().includes('home')) return 'Home Insurance';
    return 'Insurance Policy';
  };

  //for getting the intial of the world
const getInitials = (name = "") => {
  if (!name) return "?";

  const parts = name.trim().split(" ").filter(Boolean);

  // Case 1: First + Last name available
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // Case 2: Only first name → return first 2 letters
  return parts[0].substring(0, 2).toUpperCase();
};
  // console.log("company list", categories);
  return (
    <div className="dashboard-layout d-flex">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="main-content p-4">
        <div className="header-with-dropdown">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h2 className="fw-bold h_Dark">{getGreeting()}{getTimeEmoji()},{formatName(user?.name)}</h2>
              <p className="text-muted">Welcome back to your Dashboard</p>
            </div>
            
            <div className="user-profile-dropdown-container" ref={dropdownRef}>
              <div 
                className="user-profile-card bg-white d-flex align-items-center gap-1 shadow-sm position-relative"
                onClick={toggleDropdown}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && toggleDropdown()}
              >
                {/* User Avatar Circle with Initial */}
                <div className="user-avatar-circle">
                  {getInitials(user?.name)}
                </div>
                
                <div className="flex-grow-1">
                  <div className="fw-bold text-capitalize user-name">
                    {user.name ? user?.name : "User"}
                  </div>
                  <div className="text-muted small user-email">
                    {user.email ? user?.email : "user@example.com"}
                  </div>
                  {/* <div className="text-muted small user-id">
                    ID: {user.id ? user?.id : "USR_2024_001"}
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tab-bar-modern d-flex gap-3 mt-4 justify-content-start">
          {categories.map((cat) => (
            <div
              key={cat}
              className={`tab-item-modern d-flex align-items-center ${activeTab === cat ? 'active' : ''}`}
              onClick={() => setActiveTab(cat)}
            >
              <i className={`me-2 ${
                cat === "Motor" ? "bi bi-car-front-fill" : 
                cat === "Health" ? "bi bi-heart-fill" : 
                cat === "Term Life" ? "bi bi-shield-fill-check" : 
                cat === "Home" ? "bi bi-house-fill" : 
                "bi bi-briefcase-fill" // For other categories, use a default icon
              }`}></i>
              <span>{cat}</span>
            </div>
          ))}
        </div>

        {/* Updated Insurance Cards with Slider */}  
        <div className="insurance-slider-container">
          <div className="insurance-cards-slider">
  {FilteredPolicies?.map((item, index) => (
    <div className="insurance-card-slide" key={item._id}>
      <div
        className={`policy-card-new rounded-4 shadow-sm animate-card ${getCardColorClass(item.policyType, index)}`}
        onClick={() => handleCardClick(item)}
        style={{ cursor: 'pointer' }}
      >
        <div className="card-overlay">
          <div className="card-top d-flex align-items-center justify-content-between mb-3">
            <div className={`status-badge-new ${item.status.toLowerCase()}`}>
              {item.status}
            </div>
            <div className="policy-number text-white fw-semibold">
              {item.policyNumber}
            </div>
          </div>

          <div className="card-content">
            <div className="policy-category mb-2">
              {getCategoryLabel(item.policyType)}
            </div>
            <h5 
              className="policy-title-new mb-3" 
              title={item.productName} // Show full title on hover
            >
              {truncateTitle(item.productName, 4)}
            </h5>
            <div className="policy-details">
              <div className="premium-new mb-2">
                <span className="label">Premium:</span>
                <span className="value">₹{item.premiumAmount}</span>
              </div>
              <div className="expire-new">
                <span className="label">Expires:</span>
                <span className="value">{item.endDate}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="card-background-pattern"></div>
      </div>
    </div>
  ))}
</div>
          
          {/* Navigation Dots */}
          {FilteredPolicies?.length > 3 && (
            <div className="slider-dots mt-3 d-flex justify-content-center gap-2">
              {Array.from({ length: Math.ceil(FilteredPolicies.length / 3) }).map((_, dotIndex) => (
                <div
                  key={dotIndex}
                  className="slider-dot"
                  onClick={() => {
                    const slider = document.querySelector('.insurance-cards-slider');
                    const cardWidth = slider.querySelector('.insurance-card-slide').offsetWidth;
                    const gap = 16; // Gap between cards
                    slider.scrollTo({
                      left: dotIndex * (cardWidth + gap) * 3,
                      behavior: 'smooth'
                    });
                  }}
                ></div>
              ))}
            </div>
          )}
        </div>

       {/* Quick Actions and Notifications */}
        <div className="row g-3 mt-2">
          {/* Quick Actions */}
          <div className="col-md-6 col-12">
            <div className="quick-actions-box-modern bg-white p-4 rounded-4 shadow-sm">
              <h5 className="fw-bold mb-4">Quick Actions</h5>
              <div className="row g-3">
                <div className="col-md-6 col-6">
                  <div className="action-card-modern text-center p-3 rounded-3 h-100"   tabIndex={0}
 onClick={() => navigate("/customer/addinsurance?mode=manual")}
  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/customer/addinsurance?mode=manual"); }}
  style={{ cursor: "pointer" }}
  aria-label="Add Policy (Fill Manually)"
  >
                    <FaUserPlus size={24} className="text-success mb-2" />
                    <div className="fw-semibold text-dark">Add New Insurance</div>
                    <div className="text-muted">Register a new policy</div>
                  </div>      
                </div>
                <div className="col-md-6 col-6">
                  <div className="action-card-modern text-center p-3 rounded-3 h-100" role="button" tabIndex={0} onClick={() => navigate(`/customer/addinsurance`)}>
                    <BsShieldCheck size={24} className="text-primary mb-2" />
                    <div className="fw-semibold text-dark">Upload Insurance</div>
                    <div className="text-muted">Upload policy documents</div>
                  </div>
                </div>
                <div className="col-md-6 col-6">
                  <div className="action-card-modern text-center p-3 rounded-3 h-100" role="button" tabIndex={0} onClick={() => navigate(`/customer/mypolicies`)}
                  >
                    <MdOutlineNotificationsActive size={24} className="text-warning mb-2" />
                    <div className="fw-semibold text-dark">View Policies</div>
                    <div className="text-muted">Browse your coverage</div>
                  </div>
                </div>
                <div className="col-md-6 col-6">
                  <div className="action-card-modern text-center p-3 rounded-3 h-100" role="button" tabIndex={0} onClick={() => navigate(`/customer/due-payments`)}>
                    <BsGift size={24} className="text-info mb-2" />
                    <div className="fw-semibold text-dark">Due payment Policy</div>
                    <div className="text-muted">check Due policies</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications & Alerts */}
          <div className="col-md-6 col-12">
            <div className="notification-box-modern bg-white p-4 rounded-4 shadow-sm">
              <h5 className="fw-bold mb-4">Notifications & Alerts</h5>
              <div className="row">
                <div className="col-md-12 col-12">
                  <div className="alert-card alert-pending">
                    <div className="alert-title">Expired Policies</div>
                    <div className="alert-details"> You {expiredCount} have expired polices.</div>
                  </div>
                </div>
                <div className="col-md-12 col-12">
                  <div className="alert-card alert-due">
                    <div className="alert-title">Policy Renewals</div>
                    <div className="alert-details">{dueCount} policies expiring within 10 days</div>
                  </div>
                </div>
                <div className="col-md-12 col-12">
                  <div className="alert-card alert-completed">
                    <div className="alert-title">Upcoming Policies in 48 hours</div>
                    <div className="alert-details">{upcoming48hCount} policies have upcoming policies</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default Dashboard;
